import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, IdCard, FileText, MessageCircle, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Seo from "../components/Seo";

/**
 * Marketing Kit — downloadable assets for GPM Partners.
 * On first download we flag `partner_kit_downloaded_at` server-side to close the refund window.
 */
export default function PartnerMarketingKit() {
    const [me, setMe] = useState(null);
    useEffect(() => {
        api.get("/partner/me").then((r) => setMe(r.data)).catch(() => setMe({}));
    }, []);

    const flagDownloaded = async () => {
        if (me?.partner_kit_downloaded_at) return;
        try {
            const r = await api.post("/partner/kit-downloaded");
            setMe((m) => ({ ...m, partner_kit_downloaded_at: r.data.at }));
        } catch { /* ignore */ }
    };

    const copyText = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied!");
        flagDownloaded();
    };

    if (!me) {
        return <div className="min-h-screen bg-navy-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold-500 animate-spin" /></div>;
    }

    if (!me.is_partner || me.partner_status !== "active") {
        return (
            <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6">
                <div className="max-w-md text-center rounded-3xl glass p-8">
                    <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto" />
                    <h1 className="font-heading text-2xl font-bold text-white mt-4">Active Partner access only</h1>
                    <p className="text-white/70 mt-2 text-sm">The marketing kit is available only for active GPM Partners.</p>
                    <Link to="/partner/activate" className="mt-6 inline-block h-11 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold">Activate now</Link>
                </div>
            </div>
        );
    }

    const referralLink = me.referral_link || "";
    const waTemplate = `🏠 *Verified Property Deals on Global Property Mart*\n\nBrowse ready-to-move flats, plots & new projects. Free site visits & RERA-verified listings.\n\n👉 ${referralLink}\n\n_Shared by ${me.partner_id || "GPM Partner"}_`;
    const catalogueLine = `Looking for a property in your city? Explore GPM's verified catalogue — from budget flats to premium plots. Ref: ${me.referral_link}`;

    return (
        <div className="text-on-image min-h-screen bg-navy-950 grid-pattern px-4 sm:px-6 py-8" data-testid="partner-marketing-kit">
            <Seo title="Partner Marketing Kit — GPM" description="Download branded flyers, WhatsApp templates & your GPM Partner ID card." path="/partner/marketing-kit" />
            <div className="max-w-4xl mx-auto space-y-6">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500/90 font-extrabold">GPM Partner · Marketing Kit</div>
                    <h1 className="font-heading text-3xl font-bold text-white mt-1">Ready-to-share assets</h1>
                </div>

                {/* Partner ID card */}
                <div className="rounded-3xl glass p-0 overflow-hidden" data-testid="partner-id-card">
                    <div className="p-6 sm:p-8 bg-gradient-to-br from-navy-900 via-navy-950 to-navy-900 border-b border-gold-500/40">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500/90 font-extrabold">GPM Partner ID Card</div>
                                <div className="font-heading text-2xl font-bold text-white mt-3">{me.partner_id}</div>
                                <div className="text-sm text-white/70 mt-1">Referral Code: <span className="font-mono text-gold-500">{me.referral_code}</span></div>
                            </div>
                            <img src="/gpm-logo.png" alt="GPM" className="w-20 h-20 object-contain drop-shadow-[0_0_20px_rgba(218,165,32,0.4)]" />
                        </div>
                        <div className="text-xs text-white/60 mt-4">Valid till {new Date(me.partner_expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    </div>
                    <div className="p-4 bg-navy-950/60 flex justify-end gap-2">
                        <button onClick={() => { window.print(); flagDownloaded(); }} className="h-9 px-4 rounded-lg bg-gold-500 hover:bg-gold-400 text-navy-950 text-xs font-bold flex items-center gap-2" data-testid="print-id-card"><IdCard className="w-4 h-4" /> Print ID Card</button>
                    </div>
                </div>

                {/* WhatsApp template */}
                <KitCard icon={MessageCircle} title="WhatsApp share template" testid="wa-template">
                    <pre className="whitespace-pre-wrap text-sm text-white/85 bg-navy-900 rounded-xl p-4 border border-white/10 leading-relaxed">{waTemplate}</pre>
                    <button onClick={() => copyText(waTemplate)} className="mt-3 h-9 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-navy-950 text-xs font-bold flex items-center gap-2" data-testid="copy-wa">Copy template</button>
                </KitCard>

                {/* Catalogue caption */}
                <KitCard icon={FileText} title="Property catalogue caption (one-liner)" testid="catalogue-line">
                    <pre className="whitespace-pre-wrap text-sm text-white/85 bg-navy-900 rounded-xl p-4 border border-white/10">{catalogueLine}</pre>
                    <button onClick={() => copyText(catalogueLine)} className="mt-3 h-9 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-navy-950 text-xs font-bold flex items-center gap-2" data-testid="copy-caption">Copy caption</button>
                </KitCard>

                {/* Property flyer template (CSV-driven download) */}
                <KitCard icon={Download} title="Branded property flyers" testid="flyer-block">
                    <p className="text-sm text-white/70 leading-relaxed">Share any GPM property using its detail page — every share automatically carries your Partner attribution.</p>
                    <Link to="/properties" onClick={flagDownloaded} className="inline-block mt-3 h-9 px-4 rounded-lg bg-gold-500 hover:bg-gold-400 text-navy-950 text-xs font-bold" data-testid="browse-flyers">Browse property catalogue →</Link>
                </KitCard>

                {me.partner_kit_downloaded_at && (
                    <div className="rounded-xl border border-white/10 bg-navy-900/60 p-4 text-xs text-white/60 flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-gold-500 mt-0.5" />
                        <span>Marketing kit accessed on {new Date(me.partner_kit_downloaded_at).toLocaleString("en-IN")}. As per policy, refund is no longer eligible.</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function KitCard({ icon: Icon, title, children, testid }) {
    return (
        <div className="rounded-3xl glass p-6" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-gold-500" />
                <div className="font-heading text-lg font-bold text-white">{title}</div>
            </div>
            {children}
        </div>
    );
}
